import { useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { Link, useParams } from "react-router-dom";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import RowLoader from "../UI/RowLoader";
import EditIcon from "../Svgs/EditIcon";
import DeleteIcon from "../Svgs/DeleteIcon";
import AddOrEditSocialMedia from "./AddSocialMedia";
import { deleteSocialMediaChannel, getCourseDetails } from "../../apis";
import Swal from "sweetalert2";

const SocialMediaChannel = ({
  title,
  data,
  setCourseDetails,
  setPaginator,
  className = "tw-container tw-mx-auto tw-p-5 tw-bg-white tw-rounded-[10px] tw-shadow-md tw-relative",
}) => {
  const { courseId } = useParams();
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: "add", // 'add' or 'edit'
    channelId: null,
  });

  const handleOpen = (mode, channelId) => {
    setModalState({
      isOpen: true,
      mode,
      channelId,
    });
  };

  const handleClose = () => {
    setModalState({
      isOpen: false,
      mode: "add",
      channelId: null,
    });
  };

  const handleDeleteChannel = (channelId) => {
    if (!channelId) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        return deleteSocialMediaChannel(courseId, channelId)
          .then((response) => {
            return response.data;
          })
          .catch((error) => {
            Swal.showValidationMessage("Failed to delete the channel");
            console.error("Error deleting channel:", error);
          });
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Deleted!", "The channel has been deleted.", "success");

        // refetch data after deleting the channel
        getCourseDetails(courseId)
          .then((response) => {
            setCourseDetails(response.data.data);
            setPaginator({
              current_page: data.data.pagination.current_page,
              last_page: data.data.pagination.total_pages,
            });
          })
          .catch((error) => {
            console.error("Error fetching course details:", error);
          });
      }
    });
  };

  const hiddenColumns = ["id", "course_social_id"];
  const customColumns = useMemo(
    () => [
      {
        name: "Edit",
        selector: (row) => row.name,
        ui: (row) => (
          <button
            className="!tw-m-0 !tw-p-0"
            onClick={() => {
              handleOpen("edit", row.course_social_id);
            }}
          >
            <EditIcon width="22px" height="30px" />
          </button>
        ),
      },
      {
        name: "Delete",
        selector: (row) => row.name,
        ui: (row) => (
          <button
            className="!tw-m-0 !tw-p-0"
            onClick={() => handleDeleteChannel(row.course_social_id)}
          >
            <DeleteIcon width="17px" />
          </button>
        ),
      },
    ],
    []
  );

  const columns = useMemo(() => {
    return createColumns(data, customColumns, hiddenColumns, true).map(
      (col) => ({
        ...col,
        sortable: col.name === "Num",
        width: col.name === "Num" ? "70px" : "1fr",
        cell:
          col.name === "Logo"
            ? (row) => (
                <img
                  src={row.logo || "/imgs/courseDefaultImage.png"}
                  alt="logo"
                  className="tw-w-10"
                />
              )
            : col.name === "Link"
            ? (row) => (
                <Link target="_blank" rel="noopener noreferrer" to={row.link}>
                  {row.link}
                </Link>
              )
            : col.cell,
      })
    );
  }, [data, customColumns]);

  return (
    <section className={className}>
      <div className="tw-pb-2 tw-flex max-md:tw-flex-col max-md:tw-gap-4 justify-content-between tw-items-center tw-mb-4 tw-border-b">
        <h3 className="tw-text-xl tw-font-semibold tw-text-red-600 tw-p-0 !tw-m-0">
          {title}
        </h3>

        <button
          onClick={() => handleOpen("add")}
          className="btn-primary tw-flex tw-items-center max-md:tw-text-[13px] tw-max-h-10 tw-text-[14px] tw-px-2 tw-py-2 !tw-m-0 !tw-pe-4  tw-font-semibold !tw-rounded-lg tw-border tw-border-[#837D7D]/50 tw-shadow-[0px_1px_4px_0px_#00000040]"
        >
          <span className="material-icons tw-me-1">add</span>
          <span>Add Social Media</span>
        </button>
      </div>

      <AddOrEditSocialMedia
        isOpen={modalState.isOpen}
        onClose={handleClose}
        setCourseDetails={setCourseDetails}
        mode={modalState.mode}
        initialData={modalState.selectedData}
        channelId={modalState.channelId}
      />

      <DataTable
        columns={columns}
        data={data}
        customStyles={DATATABLE_CUSTOM_STYLES}
        progressPending={false}
        progressComponent={<RowLoader />}
      />
    </section>
  );
};

export default SocialMediaChannel;
